import type { Directive, DirectiveBinding } from 'vue';

type BreakpointKey = 'Small' | 'Medium' | 'Large' | 'ExtraLarge';

type SkeletonSize = {
	width: string;
	height: string;
};

type SkeletonSizes = Partial<Record<BreakpointKey, SkeletonSize>>;

type SkeletonMargins = Partial<Record<BreakpointKey, string>>;

export type SkeletonBinding = {
	loading: boolean;
	sizes?: SkeletonSizes;
	margins?: SkeletonMargins;
};

const defaultSize: SkeletonSize = { width: '2.5rem', height: '2.5rem' };
const defaultMargin = '0';

type ResolvedSkeletonConfig = {
	widths: Record<'sm' | 'md' | 'lg' | 'xl', string>;
	heights: Record<'sm' | 'md' | 'lg' | 'xl', string>;
	margins: Record<'sm' | 'md' | 'lg' | 'xl', string>;
};

const configClassMap = new Map<string, string>();
const elementConfigClass = new WeakMap<HTMLElement, string>();
let styleTag: HTMLStyleElement | null = null;
let classCounter = 0;

const sanitizeCssValue = (value: string): string => {
	// Prevent malformed CSS declarations while preserving normal CSS values.
	return value.replace(/[;{}]/g, '').trim();
};

const ensureStyleSheet = (): CSSStyleSheet | null => {
	if (typeof document === 'undefined') return null;

	if (!styleTag) {
		styleTag = document.createElement('style');
		styleTag.setAttribute('data-skeleton-configs', 'true');

		document.head.appendChild(styleTag);
	}

	return styleTag.sheet;
};

const resolveConfig = (value?: SkeletonBinding): ResolvedSkeletonConfig => {
	const sizes = value?.sizes ?? {};
	const margins = value?.margins ?? {};

	const sm = sizes.Small ?? defaultSize;
	const md = sizes.Medium ?? sm;
	const lg = sizes.Large ?? md;
	const xl = sizes.ExtraLarge ?? lg;

	const mSm = margins.Small ?? defaultMargin;
	const mMd = margins.Medium ?? mSm;
	const mLg = margins.Large ?? mMd;
	const mXl = margins.ExtraLarge ?? mLg;

	return {
		widths: {
			sm: sanitizeCssValue(sm.width),
			md: sanitizeCssValue(md.width),
			lg: sanitizeCssValue(lg.width),
			xl: sanitizeCssValue(xl.width),
		},
		heights: {
			sm: sanitizeCssValue(sm.height),
			md: sanitizeCssValue(md.height),
			lg: sanitizeCssValue(lg.height),
			xl: sanitizeCssValue(xl.height),
		},
		margins: {
			sm: sanitizeCssValue(mSm),
			md: sanitizeCssValue(mMd),
			lg: sanitizeCssValue(mLg),
			xl: sanitizeCssValue(mXl),
		},
	};
};

const serializeConfig = (config: ResolvedSkeletonConfig): string => {
	return [
		config.widths.sm,
		config.heights.sm,
		config.widths.md,
		config.heights.md,
		config.widths.lg,
		config.heights.lg,
		config.widths.xl,
		config.heights.xl,
		config.margins.sm,
		config.margins.md,
		config.margins.lg,
		config.margins.xl,
	].join('|');
};

const ensureConfigClass = (config: ResolvedSkeletonConfig): string => {
	const key = serializeConfig(config);
	const existingClass = configClassMap.get(key);

	if (existingClass) return existingClass;

	const className = `block${++classCounter}`;
	configClassMap.set(key, className);

	const stylesheet = ensureStyleSheet();
	if (!stylesheet) return className;

	const rule = [
		`.SkeletonBlock.${className}{`,
		`--skel-w-sm:${config.widths.sm};`,
		`--skel-h-sm:${config.heights.sm};`,
		`--skel-w-md:${config.widths.md};`,
		`--skel-h-md:${config.heights.md};`,
		`--skel-w-lg:${config.widths.lg};`,
		`--skel-h-lg:${config.heights.lg};`,
		`--skel-w-xl:${config.widths.xl};`,
		`--skel-h-xl:${config.heights.xl};`,
		`--skel-m-sm:${config.margins.sm};`,
		`--skel-m-md:${config.margins.md};`,
		`--skel-m-lg:${config.margins.lg};`,
		`--skel-m-xl:${config.margins.xl};`,
		`}`
	].join('');
	stylesheet.insertRule(rule, stylesheet.cssRules.length);

	return className;
};

const applySkeleton = (el: HTMLElement, value?: SkeletonBinding): void => {
	const loading = !!value?.loading;
	const configClass = ensureConfigClass(resolveConfig(value));
	const previousConfigClass = elementConfigClass.get(el);

	el.classList.add('SkeletonBlock');

	if (previousConfigClass && previousConfigClass !== configClass) {
		el.classList.remove(previousConfigClass);
	}

	el.classList.add(configClass);
	elementConfigClass.set(el, configClass);

	el.classList.toggle('loading', loading);
};

export const skeletonDirective: Directive<HTMLElement, SkeletonBinding> = {
	mounted: (el, binding: DirectiveBinding<SkeletonBinding>) => {
		applySkeleton(el, binding.value);
	},
	updated: (el, binding: DirectiveBinding<SkeletonBinding>) => {
		applySkeleton(el, binding.value);
	},
	unmounted: (el) => {
		const configClass = elementConfigClass.get(el);

		if (configClass) {
			el.classList.remove(configClass);
			elementConfigClass.delete(el);
		}

		el.classList.remove('loading');
	},
};
